import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ContentChild, DestroyRef, ElementRef,
  EventEmitter,
  HostListener, inject,
  Input, OnInit,
  Output
} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {CanvasZoomService} from "../../services/canvas-zoom.service";
import {TriggersService} from "../../services/triggers.service";
import {BuilderSectionsInteractionService} from "../../builder-sections-interaction.service";

@Component({
    selector: 'db-draggable-canvas',
    templateUrl: './draggable-canvas.component.html',
    styleUrl: './draggable-canvas.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DraggableCanvasComponent implements AfterViewInit, OnInit {
  private _destroyRef = inject(DestroyRef);
  private _canvasZoomService: CanvasZoomService = inject(CanvasZoomService);
  private _triggerService: TriggersService = inject(TriggersService);
  private _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _builderInteractionsService: BuilderSectionsInteractionService = inject(BuilderSectionsInteractionService);
  private _prepareDialogWasTriggered: boolean = false;

  isDragging: boolean = false;
  vibeCodingFlow: boolean = false;
  mouseDragMode: boolean = false;
  _scale: number = this._canvasZoomService.scale.value;
  _positionX: number = 0;
  _positionY: number = 0;
  draggableElement: HTMLElement;
  private _lastMouseX: number;
  private _lastMouseY: number;
  private _MouseX: number;
  private _MouseY: number;
  private _scaleInPercentage = this._canvasZoomService.scale.value * 100;
  @ContentChild('projected', {static: false}) projectedContent: ElementRef;
  @Output() canvasScale: EventEmitter<number> = new EventEmitter<number>();
  @Output() smoothTransition: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() isMobile: boolean;
  @Input() prefilledAi: boolean;
  @Input() disableDraggingEvents?: boolean = false;
  @Input() isSmoothTransition: boolean = false;

  @Input() set scaleLevel(level: number) {
    this.scaleInPercentage = level;
  }

  ngOnInit() {
    if (this.isMobile) {
      return;
    }
    this._listenPrepareDialog();
    this._listenFitToScreen();
    this._listenMinZoomTrigger();
    this._listenScrollToSection();
    this._listenScrollInAfterLoading();
  }

  private _listenPrepareDialog() {
    this._builderInteractionsService.discoverBusinessDataReady
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (value) {
          this._prepareDialogWasTriggered = true;
        }
      });
  }

  private _listenScrollInAfterLoading() {
    this._triggerService.fullyScaleIn
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.isSmoothTransition = true;
        if (this.prefilledAi && this._prepareDialogWasTriggered) {
          this.fitToScreen(true);
          return;
        }
        this.scaleInPercentage = 100;
      });
  }

  private _centerMainPage() {
    const container = this.projectedContent.nativeElement;
    console.log(container);
    const builderCanvas = document.getElementById('draggable');
    const containerWidth = container?.clientWidth;
    const containerHeight = container?.clientHeight - 75;
    const canvasWidth = builderCanvas?.clientWidth - (this.prefilledAi ? 345 : 0);
    const canvasHeight = builderCanvas?.clientHeight + 50;
    const scaleX = containerWidth / canvasWidth;
    const scaleY = containerHeight / canvasHeight;
  }

  private _listenMinZoomTrigger() {
    this._triggerService.calculateMinZoom
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(this._setGlobalZoomLevel.bind(this));
  }

  private _listenFitToScreen() {
    this._triggerService.fitToScreen
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        // Only fit to screen if prepareDialogForFreshOpen was triggered in this session
        if (this._prepareDialogWasTriggered) {
          this.fitToScreen();
        }
      });
  }

  private _setGlobalZoomLevel() {
    setTimeout(() => {
      const scaleInPercentage = this._getPageFitScale() * 100;
      // this._canvasZoomService.minZoom.next(scaleInPercentage);
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.code === 'Space' && !this.mouseDragMode) {
      this.mouseDragMode = true;
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.code === 'Space') {
      this.mouseDragMode = false;
    }
  }

  public set scale(value: number) {
    this._scale = value;
    this._canvasZoomService.scale.next(this._scale);
    this.transformElement();
  }

  public get scale() {
    return this._scale;
  }

  public set positionX(value: number) {
    this._positionX = value;
    this.transformElement();
  }

  public set positionY(value: number) {
    this._positionY = value;
    this.transformElement();
  }

  public get positionX() {
    return this._positionX;
  }

  public get positionY() {
    return this._positionY;
  }

  private _addSmoothTransition() {
    this.smoothTransition.emit(true);
    this._changeSmoothTransition();
    setTimeout(() => {
      this.smoothTransition.emit(false);
      this._changeSmoothTransition('remove');
    }, 700);
  }

  set scaleInPercentage(percentage: number) {
    if (this.isSmoothTransition) {
      this._addSmoothTransition();
    }
    this.scale = percentage / 100;
    if (percentage !== this._scaleInPercentage) {
      this.canvasScale.emit(percentage);
    }
    this._scaleInPercentage = percentage;
  }

  get scaleInPercentage(): number {
    return this._scaleInPercentage;
  }

  onMouseUp(event: MouseEvent | TouchEvent) {
    this.isDragging = false;
  }

  onMouseDown(event: MouseEvent | TouchEvent) {
    if (!this.mouseDragMode && event instanceof MouseEvent) {
      return;
    }
    if (!this.isMobile) {
      event.preventDefault();
    }
    document.activeElement['blur']();
    this.isDragging = true;
    const {clientX, clientY} = this.getClientCoordinates(event);
    this._lastMouseX = clientX;
    this._lastMouseY = clientY;
  }

  onMouseMove(event: MouseEvent | TouchEvent) {
    if (!this.isDragging) {
      return;
    }
    event.preventDefault();
    const {clientX, clientY} = this.getClientCoordinates(event);
    const deltaX = clientX - this._lastMouseX;
    const deltaY = clientY - this._lastMouseY;
    this.positionX += deltaX * 1.5;
    this.positionY += deltaY * 1.5;

    this._lastMouseX = clientX;
    this._lastMouseY = clientY;
  }

  onWheel(event: WheelEvent) {
    if (this.isMobile) {
      return;
    }
    event.preventDefault();
    let isMouse = Math.abs(event.deltaY) > 50;
    // this.draggableElement.style.transformOrigin = '';
    let originScale = this.scale;
    if (event.ctrlKey) {
      const minZoom = this._canvasZoomService.minZoom.value / 100;
      const deltaY = isMouse ? event.deltaY * -0.001 : event.deltaY * -0.01;
      if (this.scale + deltaY < minZoom) {
        return;
      }
      this.scale += deltaY;
      this.scale = Math.min(Math.max(minZoom, this.scale), 2.5);
      const rect = this.draggableElement.getBoundingClientRect();

      const scaleRatio = (this.scale) / originScale;

      const offsetX =
        (event.clientX - rect.left - rect.width / 2) * (scaleRatio - 1);
      const offsetY =
        (event.clientY - rect.top) * (scaleRatio - 1);

      this.positionX -= offsetX;
      this.positionY -= offsetY;
      this.scaleInPercentage = Math.floor(this.scale * 100);
      return;
    }
    this.positionX += event.deltaX * -0.9;
    this.positionY += event.deltaY * -0.9;
  }

  ngAfterViewInit() {
    this.draggableElement = document.getElementById('draggable');
    this.scale = this._canvasZoomService.scale.value;
    this.positionY = 0;
    if (this.isMobile) {
      this.addTouchListeners();
    }
  }

  private addTouchListeners() {
    this.projectedContent.nativeElement.addEventListener('touchstart', this.onTouchStart.bind(this), {passive: false});
    this.projectedContent.nativeElement.addEventListener('touchmove', this.onTouchMove.bind(this), {passive: false});
    this.projectedContent.nativeElement.addEventListener('touchend', this.onTouchEnd.bind(this), {passive: true});
  }

  transformElement() {
    if (this.draggableElement) {
      this.draggableElement.style.transform = `translate(${this.positionX}px, ${this.positionY}px) scale(${this.scale}) `;
    }
  }

  fitToScreen(onlyMain = false) {
    const scale = this._getPageFitScale(onlyMain);
    this.scale = onlyMain ? scale <= 0.7 ? 0.7 : scale : scale;
    this.scaleInPercentage = Math.floor(this.scale * 100);

    this.positionY = this.prefilledAi ? -150 : -50;
    this.positionX = this.prefilledAi ? -150 : 0;
    this._setGlobalZoomLevel();
  }

  private _getPageFitScale(onlyMain = false): number {
    const prefilledHeight = (window.innerHeight / 730) * 300;
    const container = this.projectedContent.nativeElement;
    const builderCanvas = document.getElementById('draggable');
    const containerWidth = container?.clientWidth;
    const containerHeight = container?.clientHeight - 75;
    const canvasWidth = builderCanvas?.clientWidth;
    const canvasHeight = builderCanvas?.clientHeight + (onlyMain ? -prefilledHeight : 50);
    const scaleX = containerWidth / canvasWidth;
    const scaleY = containerHeight / canvasHeight;
    return Math.min(scaleX, scaleY);
  }

  private _listenScrollToSection() {
    this._triggerService.scrollToSection
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(sectionId => {
        this._moveToSection(sectionId);
      });
  }

  private _moveToSection(sectionId: string) {
    this._addSmoothTransition();
    this.scale = 1;
    this.scaleInPercentage = Math.floor(this.scale * 100);
    this._cdr.detectChanges();
    setTimeout(() => {
      this._addSmoothTransition();
      this.moveTo(sectionId);
    }, 200);
  }

  private moveTo(id) {
    const parentRect = this.draggableElement.getBoundingClientRect();
    const childRect = document.getElementById(id).getBoundingClientRect();
    const leftSideElementWidth = 340;

    const parentCenterX = parentRect.width / 2;
    const parentTopY = 0; // Top edge of the parent element (transform origin is center 0)

    const childCenterX = (childRect.left - parentRect.left - leftSideElementWidth) + childRect.width / 2;
    const childCenterY = childRect.top - parentRect.top + childRect.height / 2;

    this.positionX = parentCenterX - childCenterX - childRect.width;
    this.positionY = parentTopY - (childCenterY - parentRect.height / 2) - childRect.height / 2;
  }

  private _changeSmoothTransition(action = 'add') {
    this.draggableElement.classList[action]('smooth-transition');
  }

  public onTouchStart(event: TouchEvent) {
    this.isDragging = true;
    this.onMouseDown(event);
  }

  public onTouchMove(event: TouchEvent) {
    if (!this.isDragging) return;
    const touch = event.touches[0];
    const deltaX = touch.clientX - this._lastMouseX;
    const deltaY = touch.clientY - this._lastMouseY;
    this.positionX += deltaX * 1.5;
    this.positionY += deltaY * 1.5;

    this._lastMouseX = touch.clientX;
    this._lastMouseY = touch.clientY;

    this.transformElement();
  }

  public onTouchEnd(event: TouchEvent) {
    this.isDragging = false;
    this.onMouseUp(event);
  }

  private getClientCoordinates(event: MouseEvent | TouchEvent): { clientX: number, clientY: number } {
    if (event instanceof MouseEvent) {
      return {clientX: event.clientX, clientY: event.clientY};
    } else {
      const touch = event.touches[0] || event.changedTouches[0];
      return {clientX: touch.clientX, clientY: touch.clientY};
    }
  }
}
